import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { FabricLobbyConfigService, GameSelector } from "@obg/gaming.external";
import {
	AppContextAction,
	AppError,
	CoreState,
	LastVisitedProductConfigService,
	MessageScopeType,
	MessageService,
	MessageType,
	ProductType,
} from "@obg/ngx.core";
import { Route, RouteSelector } from "@obg/ngx.router";
import { groupSwitchMap } from "@obg/rxjs";
import { combineLatest, merge, of } from "rxjs";
import { catchError, filter, map, mergeMap, switchMap, take } from "rxjs/operators";

import { CustomerConsistencyService } from "../customer-consistency/customer-consistency.service";
import { GameCollectionConfigService } from "../game-collection/game-collection.config";

import { GameAction } from "./game.action";
import { GAME_MESSAGE_CONTEXT } from "./game.const";
import { GameService } from "./game.service";

@Injectable()
export class GameEffect {
	games$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.fetchAll),
			filter(() => this.fabricLobbyConfigService.blockSSRForClients()),
			switchMap(() =>
				this.customerConsistencyService.consistentCustomer$.pipe(
					switchMap((customer) =>
						this.service.getAll(customer).pipe(
							map((games) => GameAction.fetchAllSuccess(games)),
							catchError((error: AppError) => of(GameAction.fetchAllFail(error)))
						)
					)
				)
			)
		)
	);

	gamesOfCollection$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.getByCollectionId),
			switchMap(({ collectionId, topCount }) =>
				this.customerConsistencyService.consistentCustomer$.pipe(
					switchMap((customer) =>
						this.service.getByCollection(collectionId, customer, topCount).pipe(
							map((games) => GameAction.fetchByCollectionIdSuccess(games)),
							catchError((error: AppError) => of(GameAction.fetchByCollectionIdFail(error)))
						)
					)
				)
			)
		)
	);

	game$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.getDetail),
			groupSwitchMap((x) =>
				this.service.getDetailed(x.gameId).pipe(
					map((gameDetail) => GameAction.getDetailSuccess(gameDetail)),
					catchError((error: AppError) => of(GameAction.getDetailFail(error)))
				)
			)
		)
	);

	getGames$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.getGames),
			filter(() => this.fabricLobbyConfigService.blockSSRForClients()),
			switchMap(() =>
				combineLatest([
					this.store.select(RouteSelector.getCurrent),
					this.store.select(this.gameSelector.isAllGamesFetched()),
				]).pipe(
					take(1),
					map(([route, isAllGamesFetched]) => this.determineGameFetchAction(route, isAllGamesFetched))
				)
			)
		)
	);

	getGamesByGameIds$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.getGamesByGameIds),
			mergeMap(({ gameIds }) =>
				this.service.getGamesByGameIds(gameIds).pipe(
					map((games) => GameAction.getGamesByGameIdsSuccess(games)),
					catchError((error: AppError) => of(GameAction.getGamesByGameIdsFail(error)))
				)
			)
		)
	);

	// As required by AOCC-862, the user should stay at the home page when returning from an external website (Like SportsBook
	// or Live Betting). For doing so we are resetting the saved Product (Last Visited Lobby) based in the game provider.
	gameProductResetForProviders$ = createEffect(() =>
		this.actions$.pipe(
			ofType(GameAction.getDetailSuccess),
			filter((payload) =>
				this.lastVisitedProductConfig.getListOfProvidersToReset().includes(payload.gameDetail.provider)
			),
			map(() => this.appContextAction.setProduct(ProductType.Common))
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$(
				{
					[GameAction.fetchAllFail.type]: GAME_MESSAGE_CONTEXT.getAll,
					[GameAction.getDetailFail.type]: GAME_MESSAGE_CONTEXT.getDetail,
					[GameAction.fetchByCollectionIdFail.type]: GAME_MESSAGE_CONTEXT.getByCollectionId,
				},
				MessageScopeType.Global,
				MessageType.Error
			),
			this.messageService.mapClearByContext$({
				[GameAction.fetchAll.type]: GAME_MESSAGE_CONTEXT.getAll,
				[GameAction.getDetail.type]: GAME_MESSAGE_CONTEXT.getDetail,
				[GameAction.fetchByCollectionId.type]: GAME_MESSAGE_CONTEXT.getByCollectionId,
			})
		)
	);

	determineGameFetchAction(route: Route | undefined, isAllGamesFetched: boolean) {
		const isFlagEnabled = this.service.getFetchOnlyNeededGamesFeatureFlag()?.enabled ?? false;

		if (!route) {
			return GameAction.getAll();
		}

		if (isFlagEnabled && !isAllGamesFetched) {
			const collection = this.gameCollectionConfigService.getRouteToCollectionMapping()?.[route?.name];
			if (collection) {
				return GameAction.fetchByCollectionId(collection);
			}
		}

		return GameAction.getAll();
	}

	constructor(
		private actions$: Actions,
		private service: GameService,
		private messageService: MessageService,
		private appContextAction: AppContextAction,
		private lastVisitedProductConfig: LastVisitedProductConfigService,
		private customerConsistencyService: CustomerConsistencyService,
		private store: Store<CoreState>,
		private gameCollectionConfigService: GameCollectionConfigService,
		private gameSelector: GameSelector,
		private fabricLobbyConfigService: FabricLobbyConfigService
	) {}
}
